import { format } from 'date-fns';

/**
 * Funções utilitárias para uso no módulo de Pagamentos
 */

/**
 * Formata um valor para moeda brasileira
 * @param {number} value Valor a ser formatado
 * @returns {string} Valor formatado como moeda
 */
export const formatCurrency = (value) => {
  if (!value && value !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

/**
 * Formata uma data para o formato brasileiro
 * @param {string} dateString Data em formato string
 * @returns {string} Data formatada
 */
export const formatDate = (dateString) => {
  if (!dateString) return '-';
  
  try {
    // Se a string de data já estiver no formato dd/mm/yyyy
    if (typeof dateString === 'string' && dateString.includes('/')) {
      const partes = dateString.split('/');
      // Se tiver 3 partes (dia, mês, ano), manter o formato atual
      if (partes.length === 3) {
        return dateString; // Já está no formato correto
      }
    }
    
    // Tentar converter a data
    const date = new Date(dateString);
    
    // Verificar se a data é válida
    if (isNaN(date.getTime())) {
      console.warn('Data inválida recebida:', dateString);
      return '-';
    }
    
    // Formatar a data no padrão brasileiro
    return format(date, 'dd/MM/yyyy');
  } catch (error) {
    console.error('Erro ao formatar data:', error, 'Data recebida:', dateString);
    return '-';
  }
};

/**
 * Calcula os juros automáticos para parcelas atrasadas usando as regras de juros do sistema
 * @param {Array} parcelas Lista de parcelas selecionadas
 * @param {Array} emprestimos Lista de empréstimos disponíveis
 * @returns {Object} Objeto com detalhes dos juros calculados por parcela
 */
export const calcularJurosAutomaticos = async (parcelas, emprestimos) => {
  console.log('DEBUG: Início do cálculo de juros automáticos', { parcelas, emprestimos });
  if (!parcelas || !parcelas.length) return {};
  
  try {
    // Buscar as regras de juros ativas do sistema
    const response = await fetch('/regras-juros');
    const regrasJuros = await response.json();
    const regrasAtivas = regrasJuros.filter(r => r.ativo);
    
    const hoje = new Date();
    const jurosCalculados = {};
    
    for (const parcela of parcelas) {
      console.log('DEBUG: Analisando parcela', parcela);
      // Verificar se a parcela está atrasada
      function zerarHora(date) {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
      }
      const vencimentoZerado = zerarHora(new Date(parcela.data_vencimento));
      const hojeZerado = zerarHora(new Date());
      const estaAtrasada = parcela.status === 'atrasado' || 
                   (vencimentoZerado < hojeZerado && parcela.status !== 'pago');
      
      if (estaAtrasada) {
        // Calcular dias de atraso
        const umDia = 24 * 60 * 60 * 1000; // ms em um dia
        const diasAtraso = Math.floor((hojeZerado - vencimentoZerado) / umDia);
        console.log('DEBUG: Parcela atrasada', { id: parcela.id, diasAtraso, vencimento: parcela.data_vencimento, hoje: hojeZerado.toISOString().split('T')[0] });
        
        // Buscar o empréstimo relacionado à parcela
        const emprestimo = emprestimos?.find(e => e.id === parcela.id_emprestimo);
        if (!emprestimo) continue;
        
        const valorParcela = parseFloat(parcela.valor_original || parcela.valor);
        const valorTotalEmprestimo = parseFloat(emprestimo.valor_total);
        
        // Encontrar a regra aplicável baseada no valor total do empréstimo
        const regraAplicavel = regrasAtivas.find(regra => {
          const valorMinimo = parseFloat(regra.valor_minimo);
          const valorMaximo = regra.valor_maximo ? parseFloat(regra.valor_maximo) : Infinity;
          return valorTotalEmprestimo >= valorMinimo && valorTotalEmprestimo <= valorMaximo;
        });
        
        // Se não encontrou regra aplicável, usar a taxa do empréstimo como fallback
        if (!regraAplicavel) {
          const taxaJuros = emprestimo.taxa_juros || 0;
          const valorJuros = parseFloat(((parcela.valor_original || parcela.valor) * (taxaJuros / 100)).toFixed(2));
          
          jurosCalculados[parcela.id] = {
            diasAtraso,
            taxaJuros,
            valorJuros,
            valorOriginal: parcela.valor_original || parcela.valor,
            tipoRegra: 'taxa_emprestimo'
          };
          continue;
        }
        
        // Calcular o valor dos juros conforme o tipo de regra
        let valorJuros = 0;
        let taxaEfetiva = 0;
        
        if (regraAplicavel.tipo === 'percentual') {
          // Escolhe a base de cálculo baseada na configuração da regra
          const baseCalculo = regraAplicavel.base_calculo === 'valor_total' ? valorTotalEmprestimo : valorParcela;
          
          // Para regras percentuais, calcula sobre a base de cálculo selecionada
          const taxaDiaria = parseFloat(regraAplicavel.taxa) / 100;
          taxaEfetiva = taxaDiaria * 100;
          valorJuros = baseCalculo * taxaDiaria * diasAtraso;
        } else {
          // Para regras de valor fixo, multiplica o valor fixo pelos dias de atraso
          const valorFixo = parseFloat(regraAplicavel.taxa);
          valorJuros = valorFixo * diasAtraso;
          // Calcular taxa efetiva para exibição (em relação à parcela)
          taxaEfetiva = (valorJuros / valorParcela) * 100;
        }
        
        jurosCalculados[parcela.id] = {
          diasAtraso,
          taxaJuros: taxaEfetiva,
          valorJuros,
          valorOriginal: valorParcela,
          tipoRegra: regraAplicavel.tipo,
          baseCalculo: regraAplicavel.base_calculo,
          regraAplicada: regraAplicavel.id
        };
      }
    }
    
    return jurosCalculados;
  } catch (error) {
    console.error('Erro ao calcular juros automáticos:', error);
    // Em caso de erro, retornar ao cálculo simples com a taxa do empréstimo
    const hoje = new Date();
    const jurosCalculados = {};
    
    parcelas.forEach(parcela => {
      // Verificar se a parcela está atrasada
      const vencimento = new Date(parcela.data_vencimento);
      const estaAtrasada = parcela.status === 'atrasado' || 
                   (vencimento < hoje && parcela.status !== 'pago');
      
      if (estaAtrasada) {
        const umDia = 24 * 60 * 60 * 1000; // ms em um dia
        const diasAtraso = Math.floor((hoje - vencimento) / umDia);
        
        const emprestimo = emprestimos?.find(e => e.id === parcela.id_emprestimo);
        const taxaJuros = emprestimo ? emprestimo.taxa_juros : 0;
        
        const valorJuros = parseFloat(((parcela.valor_original || parcela.valor) * (taxaJuros / 100)).toFixed(2));
        
        jurosCalculados[parcela.id] = {
          diasAtraso,
          taxaJuros,
          valorJuros,
          valorOriginal: parcela.valor_original || parcela.valor,
          tipoRegra: 'taxa_emprestimo',
          observacao: 'Usando taxa do empréstimo (fallback por erro)'
        };
      }
    });
    
    return jurosCalculados;
  }
};

/**
 * Objeto PagamentoHelper com funções auxiliares
 */
export const PagamentoHelper = () => {
  // Implementau00e7u00e3o direta do cu00e1lculo de juros
  const calcularJurosAutomaticosSimples = (parcelas, emprestimos) => {
    const jurosCalculados = {};
    
    parcelas.forEach(parcela => {
      const vencimento = new Date(parcela.data_vencimento);
      const hoje = new Date();
      const estaAtrasada = parcela.status === 'atrasado' || 
                (vencimento < hoje && parcela.status !== 'pago');
      
      if (estaAtrasada) {
        const umDia = 24 * 60 * 60 * 1000; // ms em um dia
        const diasAtraso = Math.floor((hoje - vencimento) / umDia);
        
        const valorParcela = parseFloat(parcela.valor_original || parcela.valor);
        
        // Aplicar regras diretamente
        let valorJuros = 0;
        let taxaEfetiva = 0;
        
        if (valorParcela >= 600) {
          // Para parcelas acima ou igual a R$ 600, aplica 30%
          taxaEfetiva = 30;
          valorJuros = valorParcela * (taxaEfetiva / 100);
        } else {
          // Para parcelas abaixo de R$ 600, valor fixo de R$ 35,00
          valorJuros = 35;
          taxaEfetiva = (valorJuros / valorParcela) * 100;
        }
        
        jurosCalculados[parcela.id] = {
          diasAtraso,
          taxaJuros: taxaEfetiva,
          valorJuros,
          valorOriginal: valorParcela
        };
      }
    });
    
    return jurosCalculados;
  };
  
  return {
    calcularJurosAutomaticos: calcularJurosAutomaticosSimples, // Substituir a implementau00e7u00e3o
    formatCurrency,
    formatDate
  };
};
